import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {

  ClipboardList, Beaker, FlaskConical, BarChart3, Plus, Trash2, Save, Loader2,
  CheckCircle2, XCircle, AlertTriangle, LogOut, Sun, Moon, Database, Table as TableIcon,
  TrendingUp, Lock, ShieldAlert, Settings2, Target, FileText, Printer
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ScatterChart, Scatter, Area, ComposedChart, AreaChart
} from 'recharts';
import { getApiBase } from '../../lib/apiBase';

const API = getApiBase();
const fetchApi = async (url, opts = {}) => {
  const token = localStorage.getItem('dd-token');
  const headers = { 'Content-Type': 'application/json', ...opts.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}${url}`, { ...opts, headers });
  if (res.status === 401) throw new Error('AUTH');
  if (res.status === 403) throw new Error('TRIAL_EXPIRED');
  const text = await res.text();
  let data;
  try { data = JSON.parse(text); } catch { data = {}; }
  if (!res.ok) throw new Error(data.detail || 'Request failed');
  return data;
};

const TABS = [
  { id: 'project', label: 'Project Info', icon: ClipboardList },
  { id: 'mix', label: 'Mix Design', icon: Beaker },
  { id: 'samples', label: 'Samples & Results', icon: FlaskConical },
  { id: 'regime', label: 'Compliance Setup', icon: Settings2 },
  { id: 'projection', label: 'Strength Projection', icon: Target },
  { id: 'compliance', label: 'Compliance', icon: TableIcon },
  { id: 'analysis', label: 'Charts & Analysis', icon: BarChart3 },
  { id: 'report', label: 'Compliance Report', icon: FileText },
];

const TH = {
  dark: {
    bg: '#080a0f', bar: '#0f1118', card: '#12141c', cardBorder: 'rgba(255,255,255,0.06)',
    text: '#e8eaed', textMuted: '#8b8fa3', textDim: '#4a4e63', accent: '#10b981',
    accentBg: 'rgba(16,185,129,0.1)',
    inputBg: 'rgba(255,255,255,0.03)', inputBorder: 'rgba(255,255,255,0.08)',
    vBg: 'rgba(16,185,129,0.12)', nvBg: 'rgba(239,68,68,0.12)', vText: '#10b981', nvText: '#ef4444',
    tableBorder: 'rgba(255,255,255,0.06)', tableStripe: 'rgba(255,255,255,0.02)',
    sectionLabel: '#10b981', groupLine1: '#3b82f6', groupLine2: '#f59e0b', groupLine3: '#8b5cf6',
  },
  light: {
    bg: '#f5f7fa', bar: '#ffffff', card: '#ffffff', cardBorder: '#e5e7eb',
    text: '#111827', textMuted: '#6b7280', textDim: '#9ca3af', accent: '#059669',
    accentBg: 'rgba(5,150,105,0.06)',
    inputBg: '#f9fafb', inputBorder: '#d1d5db',
    vBg: '#dcfce7', nvBg: '#fee2e2', vText: '#16a34a', nvText: '#dc2626',
    tableBorder: '#e2e8f0', tableStripe: '#f8fafc',
    sectionLabel: '#059669', groupLine1: '#2563eb', groupLine2: '#d97706', groupLine3: '#7c3aed',
  },
};

const GROUP_COLORS = ['#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

export const IdentityTesting = ({ embedded = false, forcedTab = null, onTabChange = null }) => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(forcedTab || 'project');
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [msg, setMsg] = useState('');
  const [theme, setTheme] = useState(() => localStorage.getItem('dd-it-theme') || 'dark');
  const [demoLoading, setDemoLoading] = useState(false);
  const t = TH[theme];

  // Sync tab from parent when embedded
  useEffect(() => { if (embedded && forcedTab && forcedTab !== tab) setTab(forcedTab); }, [forcedTab, embedded]);
  const changeTab = (newTab) => { setTab(newTab); if (onTabChange) onTabChange(newTab); };

  useEffect(() => { localStorage.setItem('dd-it-theme', theme); }, [theme]);
  useEffect(() => { if (!embedded && !authLoading && !user) navigate('/prioris'); }, [user, authLoading, navigate, embedded]);

  const loadProjects = useCallback(async () => {
    try {
      const data = await fetchApi('/api/it/projects');
      setProjects(data);
      if (data.length > 0 && !activeProject) setActiveProject(data[0].id);
    } catch (e) { if (e.message === 'AUTH') navigate('/auth/login'); }
  }, [navigate, activeProject]);

  useEffect(() => { if (user) loadProjects(); }, [user, loadProjects]);

  const loadDemo = async () => {
    setDemoLoading(true);
    try {
      const res = await fetchApi('/api/it/demo-data', { method: 'POST' });
      if (res.project_id) { const data = await fetchApi('/api/it/projects'); setProjects(data); setActiveProject(res.project_id); setTab('compliance'); }
      setMsg(res.message);
    } catch (e) { setMsg(e.message); }
    setDemoLoading(false);
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center" style={{ background: t.bg }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: t.accent }} /></div>;
  if (!user) return null;
  const trialDaysLeft = user.trial_expires_at ? Math.max(0, Math.ceil((new Date(user.trial_expires_at) - new Date()) / 86400000)) : 35;
  const isTrialExpired = trialDaysLeft === 0;

  return (
    <div className={`${embedded ? "min-h-full prioris-qc-embedded" : "min-h-screen pt-16"}`} style={{ background: t.bg, fontFamily: "'Plus Jakarta Sans', sans-serif", color: t.text, transition: 'background 0.3s, color 0.3s' }}>
      {/* Header bar — hidden when embedded */}
      {!embedded && (
        <div style={{ background: t.bar, borderBottom: `1px solid ${t.cardBorder}` }} className="px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold" data-testid="it-title">Identity Testing Checker</h1>
              <p className="text-xs" style={{ color: t.textDim }}>EN 206 Annex B &middot; Shewhart Control Chart &middot; {isTrialExpired ? 'Trial expired' : `${trialDaysLeft} days left`}</p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-2 rounded-full" style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} data-testid="theme-toggle">
                {theme === 'dark' ? <Sun className="w-4 h-4" style={{ color: '#f59e0b' }} /> : <Moon className="w-4 h-4" style={{ color: '#6366f1' }} />}
              </button>
              <span className="text-xs" style={{ color: t.textMuted }}>{user.email}</span>
              <Button variant="ghost" size="sm" onClick={logout} style={{ color: t.textMuted }} data-testid="it-logout"><LogOut className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      )}

      {isTrialExpired && (
        <div data-testid="trial-expired-banner" className="px-4 py-4" style={{ background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)', borderBottom: '1px solid rgba(239,68,68,0.3)' }}>
          <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <ShieldAlert className="w-5 h-5 text-red-300 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-white">Your 35-day free trial has ended</p>
                <p className="text-xs text-red-200">Your data is safe and accessible in read-only mode. Upgrade to Pro to continue adding and editing data.</p>
              </div>
            </div>
            <a href="/prioris" className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold bg-white text-red-900 hover:bg-red-50 transition-colors" data-testid="trial-upgrade-btn">
              <Lock className="w-4 h-4" /> Upgrade to Pro
            </a>
          </div>
        </div>
      )}

      <div className={`${embedded ? 'max-w-none px-5 py-4' : 'max-w-7xl mx-auto px-4 py-6'}`} style={embedded ? { fontSize: '12px' } : {}}>
        <div className={`flex items-center gap-3 mb-4 flex-wrap ${embedded ? 'text-[11px]' : ''}`}>
          <select value={activeProject || ''} onChange={e => setActiveProject(e.target.value)} style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} className={`rounded px-3 py-1.5 ${embedded ? 'text-[11px]' : 'text-sm'}`} data-testid="project-selector">
            {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
            {projects.length === 0 && <option value="">No projects</option>}
          </select>
          <Button size="sm" disabled={isTrialExpired} onClick={async () => { try { const p = await fetchApi('/api/it/projects', { method: 'POST', body: JSON.stringify({ project_name: `Project ${projects.length + 1}`, specified_strength: 30 }) }); setProjects(prev => [p, ...prev]); setActiveProject(p.id); changeTab('project'); } catch (e) { setMsg(e.message === 'TRIAL_EXPIRED' ? 'Trial expired — upgrade to Pro to create projects.' : e.message); } }} className={`bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed ${embedded ? 'text-[10px] px-2 py-1' : ''}`} data-testid="new-project-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New Project</Button>
          <Button size="sm" variant="outline" onClick={loadDemo} disabled={demoLoading || isTrialExpired} style={{ borderColor: t.accent, color: isTrialExpired ? t.textDim : t.accent, opacity: isTrialExpired ? 0.4 : 1 }} className={embedded ? 'text-[10px] px-2 py-1' : ''} data-testid="load-demo-btn">{demoLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Database className="w-3.5 h-3.5 mr-1" />} Load Demo Data</Button>
        </div>

        {/* Horizontal tab bar — hidden when embedded (sidebar has tabs) */}
        {!embedded && (
          <div className="flex gap-1 mb-6 overflow-x-auto" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
            {TABS.map(tb => (
              <button key={tb.id} onClick={() => changeTab(tb.id)} data-testid={`tab-${tb.id}`}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap"
                style={{ color: tab === tb.id ? t.accent : t.textMuted, borderBottom: tab === tb.id ? `2px solid ${t.accent}` : '2px solid transparent' }}>
                <tb.icon className="w-4 h-4" /> {tb.label}
              </button>
            ))}
          </div>
        )}

        {msg && <div className="mb-4 p-3 rounded text-sm" style={{ background: msg.includes('expired') || msg.includes('Expired') || msg.includes('Trial') ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', color: msg.includes('expired') || msg.includes('Expired') || msg.includes('Trial') ? '#ef4444' : t.accent }}>{msg}<button onClick={() => setMsg('')} className="ml-2">&times;</button></div>}

        {activeProject && tab === 'project' && <ProjectTab projectId={activeProject} onSaved={loadProjects} t={t} readOnly={isTrialExpired} />}
        {activeProject && tab === 'mix' && <MixDesignTab projectId={activeProject} t={t} readOnly={isTrialExpired} />}
        {activeProject && tab === 'samples' && <SamplesGridTab projectId={activeProject} t={t} readOnly={isTrialExpired} />}
        {activeProject && tab === 'regime' && <RegimeSetupTab projectId={activeProject} t={t} readOnly={isTrialExpired} onSaved={loadProjects} />}
        {activeProject && tab === 'projection' && <StrengthProjectionTab projectId={activeProject} t={t} />}
        {activeProject && tab === 'compliance' && <ComplianceTab projectId={activeProject} t={t} />}
        {activeProject && tab === 'analysis' && <AnalysisTab projectId={activeProject} t={t} />}
        {activeProject && tab === 'report' && <ComplianceReportTab projectId={activeProject} t={t} />}
        {!activeProject && <div className="text-center py-20" style={{ color: t.textDim }}>{isTrialExpired ? 'Your trial has expired. Existing data is still viewable.' : 'Create a project or load demo data to get started.'}</div>}
      </div>
    </div>
  );
};

/* ─── Helpers ─── */
const iS = (t) => ({ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text });
const VBadge = ({ pass, t }) => (
  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide"
    style={{ background: pass ? t.vBg : t.nvBg, color: pass ? t.vText : t.nvText }}>
    {pass ? 'V' : 'NV'}
  </span>
);

/* ═══════════════ TAB 1: PROJECT INFO ═══════════════ */
const ProjectTab = ({ projectId, onSaved, t, readOnly }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { fetchApi(`/api/it/projects/${projectId}`).then(setForm).catch(() => {}); }, [projectId]);
  const save = async () => { if (readOnly) return; setSaving(true); try { await fetchApi(`/api/it/projects/${projectId}`, { method: 'PUT', body: JSON.stringify(form) }); onSaved?.(); } catch {} setSaving(false); };
  const set = (k) => (e) => { if (readOnly) return; setForm(prev => ({ ...prev, [k]: e.target.value })); };

  return (
    <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
      <CardHeader><CardTitle style={{ color: t.text }} className="flex items-center gap-2"><ClipboardList className="w-5 h-5" style={{ color: t.accent }} /> Project Information</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          {[['Project Name','project_name','text'],['Date','date','date'],['Contractor','contractor','text'],['Location','location','text'],['Structural Member','structural_member','text']].map(([l,k,tp]) =>
            <div key={k} className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>{l}</Label><input type={tp} value={form[k]||''} onChange={set(k)} readOnly={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), opacity: readOnly ? 0.6 : 1, cursor: readOnly ? 'not-allowed' : undefined }} data-testid={`project-${k}`} /></div>
          )}
          <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>f<sub>ck</sub> (MPa)</Label><input type="number" value={form.specified_strength||''} onChange={e => { if (readOnly) return; setForm(p=>({...p, specified_strength:parseFloat(e.target.value)||0})); }} readOnly={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), opacity: readOnly ? 0.6 : 1, cursor: readOnly ? 'not-allowed' : undefined }} data-testid="project-fck" /></div>
          <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>Exposure Class</Label><input value={form.exposure_class||''} onChange={set('exposure_class')} readOnly={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), opacity: readOnly ? 0.6 : 1, cursor: readOnly ? 'not-allowed' : undefined }} data-testid="project-exposure" /></div>
        </div>
        <Button onClick={save} disabled={saving || readOnly} className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed" data-testid="project-save">{readOnly ? <><Lock className="w-4 h-4 mr-2" /> Read Only</> : saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}</Button>
      </CardContent>
    </Card>
  );
};

/* ═══════════════ TAB 2: MIX DESIGN ═══════════════ */
const MixDesignTab = ({ projectId, t, readOnly }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [approvedMixes, setApprovedMixes] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  useEffect(() => { fetchApi(`/api/it/projects/${projectId}/mix-design`).then(d => { if (d && Object.keys(d).length) setForm(d); }).catch(() => {}); }, [projectId]);
  // Load approved MA concrete mixes for import dropdown
  useEffect(() => {
    fetchApi('/api/prioris/ma/approved-mixes')
      .then(d => setApprovedMixes(d.mixes || []))
      .catch(() => setApprovedMixes([]));
  }, []);
  const save = async () => { if (readOnly) return; setSaving(true); try { const res = await fetchApi(`/api/it/projects/${projectId}/mix-design`, { method: 'POST', body: JSON.stringify(form) }); setForm(res); } catch {} setSaving(false); };
  const importFromMa = (mixId) => {
    if (!mixId) return;
    const found = approvedMixes.find(m => m.id === mixId);
    if (!found?.mix_design) return;
    setImporting(true);
    // Merge: keep current mix_reference if set, otherwise prefill from MA
    setForm(prev => ({ ...prev, ...found.mix_design }));
    setImportMsg(`Imported from ${found.internal_ref} — review and click Save to persist.`);
    setTimeout(() => setImportMsg(''), 5000);
    setImporting(false);
  };
  const set = (k) => (e) => { if (readOnly) return; setForm(prev => ({ ...prev, [k]: e.target.value })); };
  const setNum = (k) => (e) => { if (readOnly) return; setForm(prev => ({ ...prev, [k]: e.target.value === '' ? 0 : parseFloat(e.target.value) })); };
  const setNumOpt = (k) => (e) => { if (readOnly) return; setForm(prev => ({ ...prev, [k]: e.target.value === '' ? null : parseFloat(e.target.value) })); };
  const roStyle = readOnly ? { opacity: 0.6, cursor: 'not-allowed' } : {};

  const cement = form.cement_content || 0, fa = form.fly_ash_content || 0, sf = form.silica_fume_content || 0, ggbs = form.ggbs_content || 0;
  const totalCm = cement + fa + sf + ggbs;
  const scmPct = totalCm > 0 ? ((fa + sf + ggbs) / totalCm * 100).toFixed(1) : '0.0';
  const wcRatio = totalCm > 0 ? ((form.water_content || 0) / totalCm).toFixed(3) : '0.000';
  const d7 = form.declared_fc7, d28 = form.declared_fc28;
  const declaredRatio = d7 && d28 && d28 > 0 ? (d7 / d28).toFixed(4) : '—';

  const field = (label, key, type='number') => (
    <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>{label}</Label>
      <input type={type} value={form[key] ?? ''} onChange={type === 'number' ? setNum(key) : set(key)} readOnly={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid={`mix-${key}`} />
    </div>
  );

  return (
    <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
      <CardHeader>
        <CardTitle style={{ color: t.text }} className="flex items-center gap-2"><Beaker className="w-5 h-5" style={{ color: t.accent }} /> Mix Design</CardTitle>
        <p className="text-xs mt-1" style={{ color: t.textDim }}>FHWA 1608 layout &middot; kg/m&sup3;</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Import from Materials Approval banner */}
        {approvedMixes.length > 0 && (
          <div className="rounded p-3 flex items-center gap-3 flex-wrap" style={{ background: 'rgba(16,185,129,0.06)', border: `1px solid ${t.accent}44` }}>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold" style={{ color: t.accent }}>Import from Materials Approval</div>
              <div className="text-[11px]" style={{ color: t.textMuted }}>Pick an approved mix submittal to pre-fill this form. You can still edit it before saving.</div>
            </div>
            <select onChange={e => importFromMa(e.target.value)} defaultValue="" disabled={readOnly || importing}
              className="rounded px-2 py-1.5 text-xs"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, minWidth: 220 }}
              data-testid="mix-import-select">
              <option value="">Select approved mix…</option>
              {approvedMixes.map(m => (
                <option key={m.id} value={m.id}>
                  {m.internal_ref} — {m.product_description || m.mix_design?.mix_reference || 'Unnamed'}
                  {m.producer ? ` · ${m.producer}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {importMsg && (
          <div className="rounded px-3 py-2 text-xs" style={{ background: 'rgba(16,185,129,0.1)', color: t.accent }}>{importMsg}</div>
        )}
        <div className="grid md:grid-cols-3 gap-4">
          {field('Mix Reference', 'mix_reference', 'text')}{field('Producer', 'producer_name', 'text')}{field('Class', 'class_of_concrete', 'text')}
        </div>
        <div>
          <h3 className="text-xs font-semibold tracking-wider mb-3" style={{ color: t.sectionLabel }}>CEMENTITIOUS MATERIALS (kg/m&sup3;)</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>Cement Type</Label>
              <select value={form.cement_type || ''} onChange={set('cement_type')} disabled={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="mix-cement-type">
                <option value="">Select...</option>{['CEM I','CEM II/A-L','CEM II/A-LL','CEM II/B-V','CEM III/A','CEM III/B','CEM IV','CEM V'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {field('Brand', 'cement_brand', 'text')}
            {field('Cement', 'cement_content')}{field('Fly Ash', 'fly_ash_content')}{field('Silica Fume', 'silica_fume_content')}{field('GGBS', 'ggbs_content')}
          </div>
          <div className="mt-2 flex gap-6 text-xs" style={{ color: t.textMuted }}>Total: <strong style={{ color: t.text }}>{totalCm.toFixed(1)}</strong> &middot; SCM: <strong style={{ color: t.text }}>{scmPct}%</strong></div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {field('Coarse Agg (SSD)', 'coarse_aggregate')}{field('Fine Agg (SSD)', 'fine_aggregate')}
          {field('Water', 'water_content')}
          <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>w/cm</Label><div className="rounded px-3 py-2 text-sm font-mono" style={iS(t)}>{wcRatio}</div></div>
          {field('Admixture Type', 'admixture_type', 'text')}{field('Dosage', 'admixture_dosage', 'text')}
        </div>
        {/* Declared strengths */}
        <div>
          <h3 className="text-xs font-semibold tracking-wider mb-3" style={{ color: t.sectionLabel }}>DECLARED STRENGTH (from initial trials / supplier)</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>7-day (MPa)</Label>
              <input type="number" step="0.1" value={form.declared_fc7 ?? ''} onChange={setNumOpt('declared_fc7')} readOnly={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="mix-declared-fc7" />
            </div>
            <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>28-day (MPa)</Label>
              <input type="number" step="0.1" value={form.declared_fc28 ?? ''} onChange={setNumOpt('declared_fc28')} readOnly={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="mix-declared-fc28" />
            </div>
            <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>90-day (MPa)</Label>
              <input type="number" step="0.1" value={form.declared_fc90 ?? ''} onChange={setNumOpt('declared_fc90')} readOnly={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="mix-declared-fc90" />
            </div>
            <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>fc7/fc28 Ratio</Label><div className="rounded px-3 py-2 text-sm font-mono" style={iS(t)} data-testid="mix-ratio">{declaredRatio}</div></div>
          </div>
        </div>
        <div>
          <h3 className="text-xs font-semibold tracking-wider mb-3" style={{ color: t.sectionLabel }}>COST DATA (Optional)</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>Cement Unit Cost ($/tonne)</Label>
              <input type="number" step="1" value={form.cement_unit_cost ?? ''} onChange={setNumOpt('cement_unit_cost')} readOnly={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="mix-cement-cost" />
            </div>
          </div>
        </div>
        <Button onClick={save} disabled={saving || readOnly} className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed" data-testid="mix-save">{readOnly ? <><Lock className="w-4 h-4 mr-2" /> Read Only</> : saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}</Button>
      </CardContent>
    </Card>
  );
};

/* ═══════════════ TAB 3: EXCEL-LIKE GRID ═══════════════ */
const SamplesGridTab = ({ projectId, t, readOnly }) => {
  const [pours, setPours] = useState([]);
  const [activePour, setActivePour] = useState(null);
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPourId, setEditingPourId] = useState(null);
  const [editingPourName, setEditingPourName] = useState('');
  const saveTimeouts = useRef({});

  const loadPours = useCallback(async () => {
    try { const data = await fetchApi(`/api/it/projects/${projectId}/pours`); setPours(data); if (data.length > 0 && !activePour) setActivePour(data[0].id); } catch {} setLoading(false);
  }, [projectId]);

  useEffect(() => { loadPours(); }, [loadPours]);
  useEffect(() => {
    if (!activePour) return;
    fetchApi(`/api/it/pours/${activePour}/samples`).then(setSamples).catch(() => {});
  }, [activePour]);

  const addPour = async () => { if (readOnly) return; try { const p = await fetchApi(`/api/it/projects/${projectId}/pours`, { method: 'POST', body: JSON.stringify({ pour_name: `Pour ${pours.length + 1}` }) }); setPours(prev => [...prev, p]); setActivePour(p.id); } catch (e) { alert(e.message === 'TRIAL_EXPIRED' ? 'Trial expired — upgrade to Pro.' : e.message); } };
  const addSample = async () => { if (!activePour || readOnly) return; try { const s = await fetchApi(`/api/it/pours/${activePour}/samples`, { method: 'POST', body: JSON.stringify({ sample_id_label: `S${String(samples.length + 1).padStart(3, '0')}`, specimen_type: 'cube' }) }); setSamples(prev => [...prev, s]); } catch (e) { alert(e.message === 'TRIAL_EXPIRED' ? 'Trial expired — upgrade to Pro.' : e.message); } };
  const deletePour = async () => {
    if (readOnly || !activePour) return;
    const pour = pours.find(p => p.id === activePour);
    const name = pour?.pour_name || `Pour ${pour?.pour_number}`;
    if (!window.confirm(`Are you sure you want to delete ${samples.length} results from pour "${name}"? This cannot be undone.`)) return;
    try { await fetchApi(`/api/it/pours/${activePour}`, { method: 'DELETE' }); setPours(prev => prev.filter(p => p.id !== activePour)); setSamples([]); setActivePour(pours.length > 1 ? pours.find(p => p.id !== activePour)?.id : null); } catch (e) { alert(e.message); }
  };

  // Live projection summary for pending samples
  const [projectionSummary, setProjectionSummary] = useState([]);
  useEffect(() => {
    fetchApi(`/api/it/projects/${projectId}/analysis`).then(d => {
      setProjectionSummary((d.projection_table || []).filter(r => r.fc7 && !r.fc28_actual && r.projected_f28));
    }).catch(() => {});
  }, [projectId, samples]);

  const startRenamePour = (pour) => {
    if (readOnly) return;
    setEditingPourId(pour.id);
    setEditingPourName(pour.pour_name || `Pour ${pour.pour_number}`);
  };

  const saveRenamePour = async () => {
    const name = editingPourName.trim();
    if (!name || !editingPourId) { setEditingPourId(null); return; }
    try {
      const updated = await fetchApi(`/api/it/pours/${editingPourId}`, { method: 'PATCH', body: JSON.stringify({ pour_name: name }) });
      setPours(prev => prev.map(p => p.id === editingPourId ? { ...p, pour_name: updated.pour_name } : p));
    } catch {}
    setEditingPourId(null);
  };

  const cancelRenamePour = () => { setEditingPourId(null); };

  const updateCell = (sampleId, field, value) => {
    if (readOnly) return;
    setSamples(prev => prev.map(s => s.id === sampleId ? { ...s, [field]: value } : s));
    clearTimeout(saveTimeouts.current[sampleId]);
    saveTimeouts.current[sampleId] = setTimeout(async () => {
      const sample = samples.find(s => s.id === sampleId);
      if (!sample) return;
      try { const res = await fetchApi(`/api/it/samples/${sampleId}`, { method: 'PUT', body: JSON.stringify({ ...sample, [field]: value }) }); setSamples(prev => prev.map(s => s.id === sampleId ? res : s)); } catch {}
    }, 800);
  };

  const deleteSample = async (id) => { if (readOnly || !window.confirm('Delete?')) return; try { await fetchApi(`/api/it/samples/${id}`, { method: 'DELETE' }); setSamples(prev => prev.filter(s => s.id !== id)); } catch {} };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: t.accent }} /></div>;

  const COLS = [
    { key: 'sample_id_label', label: 'ID', w: 'w-16', type: 'text' },
    { key: 'date_cast', label: 'Date', w: 'w-28', type: 'date' },
    { key: 'time_sampled', label: 'Time', w: 'w-20', type: 'time' },
    { key: 'temperature', label: 'T°C', w: 'w-16', type: 'number' },
    { key: 'slump_mm', label: 'Slump', w: 'w-16', type: 'number' },
    { key: 'air_content_pct', label: 'Air%', w: 'w-14', type: 'number' },
    { key: 'fc7_1', label: 'fc7,1', w: 'w-16', type: 'number' },
    { key: 'fc7_2', label: 'fc7,2', w: 'w-16', type: 'number' },
    { key: 'fc7_3', label: 'fc7,3', w: 'w-16', type: 'number' },
    { key: 'fc28_1', label: 'fc28,1', w: 'w-16', type: 'number' },
    { key: 'fc28_2', label: 'fc28,2', w: 'w-16', type: 'number' },
    { key: 'fc28_3', label: 'fc28,3', w: 'w-16', type: 'number' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          {pours.map(p => (
            editingPourId === p.id ? (
              <input
                key={p.id}
                autoFocus
                value={editingPourName}
                onChange={(e) => setEditingPourName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveRenamePour(); if (e.key === 'Escape') cancelRenamePour(); }}
                onBlur={saveRenamePour}
                className="px-2 py-1 rounded text-xs font-medium outline-none"
                style={{ background: t.inputBg, color: t.text, border: `2px solid ${t.accent}`, minWidth: 80, maxWidth: 200 }}
                data-testid={`pour-rename-input-${p.id}`}
              />
            ) : (
              <button key={p.id} onClick={() => setActivePour(p.id)} onDoubleClick={() => startRenamePour(p)} className="px-3 py-1.5 rounded text-xs font-medium transition-colors"
                style={{ background: activePour === p.id ? t.accent : t.inputBg, color: activePour === p.id ? '#fff' : t.textMuted, border: `1px solid ${activePour === p.id ? t.accent : t.inputBorder}` }}
                title="Double-click to rename"
                data-testid={`pour-tab-${p.id}`}>
                {p.pour_name || `Pour ${p.pour_number}`}
              </button>
            )
          ))}
        </div>
        <Button size="sm" onClick={addPour} disabled={readOnly} className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed" data-testid="add-pour-btn"><Plus className="w-3 h-3 mr-1" /> Pour</Button>
        <Button size="sm" variant="outline" onClick={addSample} disabled={readOnly} style={{ borderColor: `${t.accent}40`, color: readOnly ? t.textDim : t.accent, opacity: readOnly ? 0.4 : 1 }} data-testid="add-sample-btn"><Plus className="w-3 h-3 mr-1" /> Row</Button>
        {activePour && !readOnly && <Button size="sm" variant="outline" onClick={deletePour} style={{ borderColor: 'rgba(239,68,68,0.3)', color: '#ef4444' }} data-testid="delete-pour-btn"><Trash2 className="w-3 h-3 mr-1" /> Delete Pour</Button>}
        <span className="text-xs" style={{ color: t.textDim }}>{pours.length}/7 pours &middot; {samples.length} samples</span>
      </div>

      {/* Item 2: Live 28-Day Projection Summary */}
      {projectionSummary.length > 0 && (
        <div className="rounded p-3" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }} data-testid="live-projection-summary">
          <h4 className="text-[10px] font-semibold tracking-wider mb-2" style={{ color: t.sectionLabel }}>LIVE 28-DAY PROJECTIONS (pending samples)</h4>
          <div className="flex flex-wrap gap-2">
            {projectionSummary.map((r, i) => (
              <div key={i} className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded text-[11px]" style={{ background: r.status === 'on_track' ? t.vBg : r.status === 'at_risk' ? 'rgba(245,158,11,0.1)' : t.nvBg, border: `1px solid ${r.status === 'on_track' ? `${t.vText}20` : r.status === 'at_risk' ? '#f59e0b20' : `${t.nvText}20`}` }}>
                <span className="font-mono font-bold" style={{ color: t.text }}>{r.sample_id}</span>
                <span style={{ color: t.textMuted }}>f7={r.fc7}</span>
                <span className="font-bold" style={{ color: t.text }}>&rarr; {r.projected_f28}</span>
                <span style={{ color: t.textDim }}>[{r.ci_lower},{r.ci_upper}]</span>
                <span style={{ color: r.status === 'on_track' ? t.vText : r.status === 'at_risk' ? '#f59e0b' : t.nvText }}>{r.status === 'on_track' ? '✓' : r.status === 'at_risk' ? '⚠' : '✗'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Spreadsheet grid */}
      <div className="overflow-x-auto rounded" style={{ border: `1px solid ${t.cardBorder}` }}>
        <table className="w-full" style={{ borderCollapse: 'collapse', color: t.text, fontSize: '12px' }}>
          <thead>
            <tr style={{ background: t.card, borderBottom: `2px solid ${t.tableBorder}` }}>
              <th className="px-1 py-2 text-left" style={{ color: t.textMuted, width: '24px' }}>#</th>
              {COLS.map(c => <th key={c.key} className={`px-1 py-2 text-left font-semibold ${c.w}`} style={{ color: t.textMuted }}>{c.label}</th>)}
              <th className="px-1 py-2 text-center" style={{ color: t.textMuted, width: '40px' }}>fc7</th>
              <th className="px-1 py-2 text-center" style={{ color: t.textMuted, width: '40px' }}>fc28</th>
              <th className="px-1 py-2" style={{ width: '24px' }}></th>
            </tr>
          </thead>
          <tbody>
            {samples.map((s, i) => (
              <tr key={s.id} style={{ borderBottom: `1px solid ${t.tableBorder}`, background: i % 2 === 1 ? t.tableStripe : 'transparent' }}>
                <td className="px-1 py-0.5 text-center" style={{ color: t.textDim }}>{i + 1}</td>
                {COLS.map(c => (
                  <td key={c.key} className="px-0.5 py-0.5">
                    <input
                      type={c.type} step={c.type === 'number' ? '0.1' : undefined}
                      value={s[c.key] ?? ''}
                      onChange={e => {
                        if (readOnly) return;
                        const val = c.type === 'number' ? (e.target.value === '' ? null : parseFloat(e.target.value)) : e.target.value;
                        updateCell(s.id, c.key, val);
                      }}
                      readOnly={readOnly}
                      className="w-full rounded px-1.5 py-1 text-xs focus:ring-1 focus:ring-emerald-500 outline-none"
                      style={{ background: 'transparent', border: `1px solid transparent`, color: t.text, cursor: readOnly ? 'not-allowed' : undefined, opacity: readOnly ? 0.7 : 1 }}
                      onFocus={e => { if (!readOnly) { e.target.style.border = `1px solid ${t.accent}`; e.target.style.background = t.inputBg; } }}
                      onBlur={e => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent'; }}
                    />
                  </td>
                ))}
                <td className="px-1 py-0.5 text-center font-mono font-semibold" style={{ color: t.accent }}>{s.fc7_avg ?? '—'}</td>
                <td className="px-1 py-0.5 text-center font-mono font-semibold" style={{ color: t.accent }}>{s.fc28_avg ?? '—'}</td>
                <td className="px-0.5 py-0.5">{!readOnly && <button onClick={() => deleteSample(s.id)} className="p-1 opacity-30 hover:opacity-100" style={{ color: t.nvText }}><Trash2 className="w-3 h-3" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {samples.length === 0 && <div className="py-8 text-center text-xs" style={{ color: t.textDim }}>Click "+ Row" to add samples</div>}
      </div>
    </div>
  );
};

/* ═══════════════ TAB 3.5: COMPLIANCE SETUP (Regime) ═══════════════ */
const REGIMES = [
  { value: 'EN 206 Annex B', label: 'Identity Testing (EN 206 Annex B)' },
  { value: 'EN 206 Cl. 8', label: 'Production Control (EN 206 Cl. 8)' },
  { value: 'HK CoP SUC 2013', label: 'HK CoP SUC 2013' },
  { value: 'ACI 318', label: 'ACI 318 (cl. 26.12)' },
  { value: 'FHWA / PWL', label: 'US DOT / FHWA PWL' },
];

const RegimeSetupTab = ({ projectId, t, readOnly, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { fetchApi(`/api/it/projects/${projectId}`).then(setForm).catch(() => {}); }, [projectId]);
  const save = async () => { if (readOnly) return; setSaving(true); try { await fetchApi(`/api/it/projects/${projectId}`, { method: 'PUT', body: JSON.stringify(form) }); onSaved?.(); } catch {} setSaving(false); };
  const regime = form.compliance_regime || 'EN 206 Annex B';
  const roStyle = readOnly ? { opacity: 0.6, cursor: 'not-allowed' } : {};

  return (
    <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
      <CardHeader><CardTitle style={{ color: t.text }} className="flex items-center gap-2"><Settings2 className="w-5 h-5" style={{ color: t.accent }} /> Compliance Setup</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label className="text-xs font-semibold tracking-wider" style={{ color: t.sectionLabel }}>COMPLIANCE REGIME</Label>
          <select value={regime} onChange={e => { if (readOnly) return; setForm(prev => ({ ...prev, compliance_regime: e.target.value })); }} disabled={readOnly} className="mt-1.5 rounded px-3 py-2 text-sm w-full md:w-1/2" style={{ ...iS(t), ...roStyle }} data-testid="regime-selector">
            {REGIMES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <p className="text-[10px] mt-1" style={{ color: t.textDim }}>Determines which acceptance criteria are applied to your test results.</p>
        </div>

        {regime === 'HK CoP SUC 2013' && (
          <div>
            <Label className="text-xs font-semibold tracking-wider mb-2 block" style={{ color: t.sectionLabel }}>HK CoP PARAMETERS</Label>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>Element Type</Label>
                <select value={form.hk_element_type||'other'} onChange={e => { if (readOnly) return; setForm(p=>({...p, hk_element_type:e.target.value})); }} disabled={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="hk-element-type">
                  <option value="critical">Critical (masts, cantilevers, transfer)</option>
                  <option value="mass">Mass (rafts, pile caps, caissons)</option>
                  <option value="other">All other types</option>
                </select>
              </div>
              <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>Cube Size</Label>
                <select value={form.hk_cube_size||150} onChange={e => { if (readOnly) return; setForm(p=>({...p, hk_cube_size:parseInt(e.target.value)})); }} disabled={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="hk-cube-size">
                  <option value={150}>150mm</option><option value={100}>100mm</option>
                </select>
              </div>
              <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>Criterion</Label>
                <select value={form.hk_criterion||'C1'} onChange={e => { if (readOnly) return; setForm(p=>({...p, hk_criterion:e.target.value})); }} disabled={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="hk-criterion">
                  <option value="C1">C1 (default — before 40 proven results)</option><option value="C2">C2 (proven &sigma; &lt; threshold)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {regime === 'ACI 318' && (
          <div>
            <Label className="text-xs font-semibold tracking-wider mb-2 block" style={{ color: t.sectionLabel }}>ACI 318 PARAMETERS</Label>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>Unit System</Label>
                <select value={form.aci_unit||'MPa'} onChange={e => { if (readOnly) return; setForm(p=>({...p, aci_unit:e.target.value})); }} disabled={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="aci-unit">
                  <option value="MPa">MPa</option><option value="psi">psi</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {regime === 'FHWA / PWL' && (
          <div>
            <Label className="text-xs font-semibold tracking-wider mb-2 block" style={{ color: t.sectionLabel }}>FHWA / PWL PARAMETERS</Label>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>Lot Size (tests per lot)</Label>
                <input type="number" min={5} max={10} value={form.fhwa_lot_size||5} onChange={e => { if (readOnly) return; setForm(p=>({...p, fhwa_lot_size:parseInt(e.target.value)||5})); }} readOnly={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="fhwa-lot-size" />
              </div>
              <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>PWL Threshold (%)</Label>
                <input type="number" min={50} max={100} value={form.fhwa_pwl_threshold||90} onChange={e => { if (readOnly) return; setForm(p=>({...p, fhwa_pwl_threshold:parseFloat(e.target.value)||90})); }} readOnly={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="fhwa-pwl-threshold" />
              </div>
              <div className="space-y-1"><Label className="text-xs" style={{ color: t.textMuted }}>Cylinders per Sample</Label>
                <select value={form.fhwa_cylinders_per_sample||2} onChange={e => { if (readOnly) return; setForm(p=>({...p, fhwa_cylinders_per_sample:parseInt(e.target.value)})); }} disabled={readOnly} className="rounded px-3 py-2 text-sm w-full" style={{ ...iS(t), ...roStyle }} data-testid="fhwa-cylinders">
                  <option value={2}>2</option><option value={3}>3</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <Button onClick={save} disabled={saving || readOnly} className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed" data-testid="regime-save">{readOnly ? <><Lock className="w-4 h-4 mr-2" /> Read Only</> : saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}</Button>
      </CardContent>
    </Card>
  );
};

/* ═══════════════ TAB 4: STRENGTH PROJECTION ═══════════════ */
const StrengthProjectionTab = ({ projectId, t }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); fetchApi(`/api/it/projects/${projectId}/analysis`).then(setData).catch(() => {}).finally(() => setLoading(false)); }, [projectId]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: t.accent }} /></div>;
  if (!data) return null;

  const { fck, projection_table, bayesian_projection } = data;
  const pendingRows = (projection_table || []).filter(r => r.fc7 && !r.fc28_actual);
  const completedRows = (projection_table || []).filter(r => r.fc7 && r.fc28_actual);
  const hasHighRisk = pendingRows.some(r => r.early_warning_pct > 20);

  const chartData = (projection_table || []).filter(r => r.projected_f28 != null).map((r, i) => ({
    index: r.sample_id || (i + 1),
    projected: r.projected_f28,
    ci_upper: r.ci_upper,
    ci_lower: r.ci_lower,
    actual: r.fc28_actual || null,
  }));

  return (
    <div className="space-y-6">
      {/* Early Warning Alert */}
      {hasHighRisk && (
        <div className="p-4 rounded-lg flex items-start gap-3" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)' }} data-testid="early-warning-banner">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold" style={{ color: '#ef4444' }}>Early warning: projected 28-day strength may not meet conformity threshold — review mix or curing immediately</p>
            <button className="mt-2 text-xs px-3 py-1.5 rounded opacity-60 cursor-not-allowed" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }} data-testid="email-alert-btn" disabled>Email alert (coming soon)</button>
          </div>
        </div>
      )}

      {/* Model summary — no equation, no coefficients */}
      {bayesian_projection && (
        <div className="flex items-center gap-4 flex-wrap text-xs" style={{ color: t.textDim }}>
          <span>Project pairs: <strong style={{ color: t.text }}>{bayesian_projection.project_pairs}</strong></span>
          <span>Mix family: <strong style={{ color: t.text }}>{bayesian_projection.mix_family_pairs}</strong></span>
          <span className="px-2 py-0.5 rounded" style={{ background: bayesian_projection.confidence_label === 'prior-dominated' ? 'rgba(245,158,11,0.12)' : bayesian_projection.confidence_label === 'converging' ? 'rgba(59,130,246,0.12)' : 'rgba(16,185,129,0.12)', color: bayesian_projection.confidence_label === 'prior-dominated' ? '#f59e0b' : bayesian_projection.confidence_label === 'converging' ? '#3b82f6' : t.accent }}>{bayesian_projection.confidence_label === 'prior-dominated' ? 'Prior-dominated' : bayesian_projection.confidence_label === 'converging' ? 'Converging' : 'Data-driven'}</span>
        </div>
      )}

      {/* B. Projection Chart — FIRST */}
      {chartData.length > 0 && (
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">Projection Chart</CardTitle></CardHeader>
          <CardContent>
            <div className="h-80" data-testid="projection-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={t.tableBorder} />
                  <XAxis dataKey="index" stroke={t.textDim} angle={-45} textAnchor="end" fontSize={9} interval={Math.max(0, Math.floor(chartData.length / 15))} />
                  <YAxis stroke={t.textDim} domain={['auto', 'auto']} />
                  <Tooltip contentStyle={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text, fontSize: 11 }} />
                  <ReferenceLine y={fck} stroke="#6366f1" strokeDasharray="4 4" label={{ value: `fck=${fck}`, fill: '#6366f1', fontSize: 9 }} />
                  <ReferenceLine y={fck - 4} stroke="#ef4444" strokeDasharray="4 4" label={{ value: `fck-4=${fck - 4}`, fill: '#ef4444', fontSize: 9 }} />
                  <Area dataKey="ci_upper" stroke="none" fill="rgba(16,185,129,0.08)" name="CI upper" />
                  <Area dataKey="ci_lower" stroke="none" fill={t.bg} name="CI lower" />
                  <Line type="monotone" dataKey="projected" stroke={t.accent} strokeWidth={1.5} dot={false} strokeDasharray="4 2" name="Projected f28" />
                  <Line type="monotone" dataKey="actual" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} connectNulls={false} name="Actual f28" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* A. Projection Table */}
      <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }} data-testid="projection-table">
        <CardHeader><CardTitle style={{ color: t.text }} className="text-base">Strength Projection</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '12px', color: t.text }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${t.tableBorder}` }}>
                  {['Sample', 'f7 (MPa)', 'Projected f28', '80% CI', 'P(f28<fck-4)', 'Status', 'Actual f28', 'Accuracy'].map(h =>
                    <th key={h} className="px-2 py-2 text-left font-semibold whitespace-nowrap" style={{ color: t.textMuted }}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {pendingRows.length === 0 && completedRows.length === 0 && (
                  <tr><td colSpan={8} className="px-2 py-6 text-center" style={{ color: t.textDim }}>No results entered yet</td></tr>
                )}
                {pendingRows.map((r, i) => (
                  <tr key={`p-${i}`} style={{ borderBottom: `1px solid ${t.tableBorder}`, background: r.status === 'flag' ? t.nvBg : i % 2 === 1 ? t.tableStripe : 'transparent' }}>
                    <td className="px-2 py-1.5 font-mono">{r.sample_id}</td>
                    <td className="px-2 py-1.5 font-mono">{r.fc7}</td>
                    <td className="px-2 py-1.5 font-mono font-bold">{r.projected_f28}</td>
                    <td className="px-2 py-1.5 font-mono text-xs" style={{ color: t.textMuted }}>[{r.ci_lower}, {r.ci_upper}]</td>
                    <td className="px-2 py-1.5" style={{ color: r.early_warning_pct > 20 ? '#ef4444' : r.early_warning_pct > 5 ? '#f59e0b' : t.textMuted }}>{r.early_warning_pct}%</td>
                    <td className="px-2 py-1.5">
                      {r.status === 'on_track' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: t.vBg, color: t.vText }}>&#10003; On track</span>}
                      {r.status === 'at_risk' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>&#9888; At risk</span>}
                      {r.status === 'flag' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: t.nvBg, color: t.nvText }}>&#10007; Flag</span>}
                    </td>
                    <td className="px-2 py-1.5" style={{ color: t.textDim }}>—</td>
                    <td className="px-2 py-1.5" style={{ color: t.textDim }}>—</td>
                  </tr>
                ))}
                {completedRows.map((r, i) => (
                  <tr key={`c-${i}`} style={{ borderBottom: `1px solid ${t.tableBorder}`, background: i % 2 === 1 ? t.tableStripe : 'transparent' }}>
                    <td className="px-2 py-1.5 font-mono">{r.sample_id}</td>
                    <td className="px-2 py-1.5 font-mono">{r.fc7}</td>
                    <td className="px-2 py-1.5 font-mono" style={{ color: t.textMuted }}>{r.projected_f28}</td>
                    <td className="px-2 py-1.5 font-mono text-xs" style={{ color: t.textDim }}>[{r.ci_lower}, {r.ci_upper}]</td>
                    <td className="px-2 py-1.5" style={{ color: t.textDim }}>{r.early_warning_pct}%</td>
                    <td className="px-2 py-1.5"><span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: t.vBg, color: t.vText }}>&#10003;</span></td>
                    <td className="px-2 py-1.5 font-mono font-bold" style={{ color: t.accent }}>{r.fc28_actual}</td>
                    <td className="px-2 py-1.5 font-mono" style={{ color: r.accuracy_delta != null ? (Math.abs(r.accuracy_delta) < 3 ? t.vText : '#f59e0b') : t.textDim }}>{r.accuracy_delta != null ? `${r.accuracy_delta > 0 ? '+' : ''}${r.accuracy_delta}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Convergence chart */}
      {bayesian_projection?.convergence_data?.length > 1 && (
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">Forecast Confidence Over Time</CardTitle></CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={bayesian_projection.convergence_data} margin={{ top: 5, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={t.tableBorder} />
                  <XAxis dataKey="pairs" stroke={t.textDim} fontSize={10} label={{ value: 'Data pairs', position: 'insideBottom', offset: -5, fill: t.textDim, fontSize: 9 }} />
                  <YAxis stroke={t.textDim} fontSize={10} domain={['auto', 'auto']} label={{ value: 'β', angle: -90, position: 'insideLeft', fill: t.textDim, fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text, fontSize: 11 }} />
                  <ReferenceLine y={1.47} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: 'Prior β=1.47', fill: '#f59e0b', fontSize: 9 }} />
                  <Area dataKey="beta_upper" stroke="none" fill="rgba(16,185,129,0.1)" />
                  <Area dataKey="beta_lower" stroke="none" fill={t.bg} />
                  <Line type="monotone" dataKey="beta" stroke={t.accent} strokeWidth={2} dot={{ fill: t.accent, r: 3 }} name="β estimate" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ═══════════════ COMPLIANCE TAB — REGIME-AWARE ═══════════════ */
const ComplianceTab = ({ projectId, t }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchApi(`/api/it/projects/${projectId}`),
      fetchApi(`/api/it/projects/${projectId}/pours`),
      fetchApi(`/api/it/projects/${projectId}/analysis`),
    ]).then(async ([project, pours, analysis]) => {
      const allSamples = {};
      for (const p of pours) { allSamples[p.id] = await fetchApi(`/api/it/pours/${p.id}/samples`); }
      setData({ project, pours, analysis, allSamples });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: t.accent }} /></div>;
  if (!data) return null;
  const { project, pours, analysis, allSamples } = data;
  const fck = project.specified_strength || 30;
  const regime = analysis.compliance_regime || 'EN 206 Annex B';
  const rc = analysis.regime_compliance;

  const Row = ({ pass, children }) => (
    <tr style={{ borderBottom: `1px solid ${t.tableBorder}` }}>
      {children}
      <td className="px-2 py-1.5 text-center">
        <span className="text-[11px] font-bold" style={{ color: pass ? t.vText : t.nvText }}>{pass ? '✓' : '✗'}</span>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ color: t.text }}>{project.project_name}</h2>
          <p className="text-xs" style={{ color: t.textDim }}>{regime} &middot; f<sub>ck</sub> = {fck} MPa &middot; {project.exposure_class}</p>
        </div>
        {rc && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-bold" style={{ background: rc.overall_pass ? t.vBg : t.nvBg, color: rc.overall_pass ? t.vText : t.nvText }}>{rc.overall_pass ? <><CheckCircle2 className="w-3.5 h-3.5" /> PASS</> : <><XCircle className="w-3.5 h-3.5" /> FAIL</>}</span>}
      </div>

      {/* ── Annex B (default — existing per-pour tables) ── */}
      {regime === 'EN 206 Annex B' && pours.map(pour => {
        const pourSamples = allSamples[pour.id] || [];
        const pourIdentity = analysis.pour_identity_tests?.find(pt => pt.pour_id === pour.id);
        const groups = pourIdentity?.identity_result?.groups || [];
        const samplesWithFc28 = pourSamples.filter(s => s.fc28_avg != null);
        let groupAssignment = []; let idx = 0;
        for (const g of groups) { for (let i = 0; i < g.group_size; i++) { if (idx < samplesWithFc28.length) groupAssignment.push({ sample: samplesWithFc28[idx], group: g }); idx++; } }
        const samplesNoFc28 = pourSamples.filter(s => s.fc28_avg == null);
        return (
          <Card key={pour.id} style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
            <CardHeader className="pb-2"><div className="flex items-center justify-between"><div><CardTitle className="text-base" style={{ color: t.text }}>{pour.pour_name || `Pour ${pour.pour_number}`}</CardTitle><p className="text-xs" style={{ color: t.textDim }}>{pour.pour_date} &middot; {pour.volume_m3} m&sup3; &middot; {pourSamples.length} samples</p></div>{pourIdentity && groups.length > 0 && <VBadge pass={pourIdentity.identity_result.overall_pass} t={t} />}</div></CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full" style={{ borderCollapse: 'collapse', color: t.text, fontSize: '11px' }}>
                <thead><tr style={{ borderBottom: `2px solid ${t.tableBorder}` }}>{['#','Sample','fc28 Avg','fc28 ≥ fck-4','Group Mean','fcm ≥ fck+1','Result'].map(h => <th key={h} className="px-2 py-2 text-left font-semibold whitespace-nowrap" style={{ color: t.textMuted }}>{h}</th>)}</tr></thead>
                <tbody>
                  {groupAssignment.length === 0 && samplesNoFc28.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center" style={{ color: t.textDim }}>No results entered yet</td></tr>}
                  {(() => { let rows = [], prev = null;
                    for (let gi = 0; gi < groupAssignment.length; gi++) { const { sample: s, group: g } = groupAssignment[gi]; const isNew = g.group_number !== prev;
                      if (isNew && prev !== null) rows.push(<tr key={`sep-${g.group_number}`}><td colSpan={7} style={{ height: '2px', background: GROUP_COLORS[(g.group_number - 1) % GROUP_COLORS.length], padding: 0 }}></td></tr>);
                      prev = g.group_number; const ip = s.fc28_avg >= (fck - 4);
                      rows.push(<tr key={s.id} style={{ borderBottom: `1px solid ${t.tableBorder}`, background: gi % 2 === 1 ? t.tableStripe : 'transparent' }}><td className="px-2 py-1.5" style={{ color: t.textDim }}>{gi + 1}</td><td className="px-2 py-1.5 font-mono">{s.sample_id_label}</td><td className="px-2 py-1.5 font-mono font-bold">{s.fc28_avg}</td><td className="px-2 py-1.5"><VBadge pass={ip} t={t} /> <span className="text-[10px]" style={{ color: t.textDim }}>{s.fc28_avg} ≥ {(fck-4).toFixed(1)}</span>{!ip && <span className="block text-[10px]" style={{ color: t.nvText }}>Individual result {s.fc28_avg} MPa is below fck-4 = {(fck-4).toFixed(1)} MPa</span>}</td>
                        {isNew ? <><td className="px-2 py-1.5 font-mono font-bold" rowSpan={g.group_size} style={{ verticalAlign: 'middle', borderLeft: `2px solid ${GROUP_COLORS[(g.group_number - 1) % GROUP_COLORS.length]}` }}>{g.mean}</td><td className="px-2 py-1.5" rowSpan={g.group_size} style={{ verticalAlign: 'middle' }}>{g.group_size > 1 ? <><VBadge pass={g.criterion_1.pass} t={t} /> <span className="text-[10px]" style={{ color: t.textDim }}>{g.mean}≥{(fck+1).toFixed(1)}</span>{!g.criterion_1.pass && <span className="block text-[10px]" style={{ color: t.nvText }}>Group mean {g.mean} MPa below fck+1 = {(fck+1).toFixed(1)} MPa</span>}</> : <span style={{ color: t.textDim }}>n=1</span>}</td><td className="px-2 py-1.5" rowSpan={g.group_size} style={{ verticalAlign: 'middle' }}><VBadge pass={g.pass} t={t} /></td></> : null}
                      </tr>);
                    }
                    for (const s of samplesNoFc28) rows.push(<tr key={s.id} style={{ borderBottom: `1px solid ${t.tableBorder}`, opacity: 0.5 }}><td className="px-2 py-1.5" style={{ color: t.textDim }}>—</td><td className="px-2 py-1.5 font-mono">{s.sample_id_label}</td><td colSpan={5} className="px-2 py-1.5 text-center" style={{ color: t.textDim }}>Awaiting 28d results</td></tr>);
                    return rows; })()}
                </tbody>
              </table>
              {groups.length > 0 && <div className="mt-3 flex flex-wrap gap-2 pt-2" style={{ borderTop: `1px solid ${t.tableBorder}` }}>{groups.map(g => <div key={g.group_number} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-medium" style={{ background: g.pass ? t.vBg : t.nvBg, color: g.pass ? t.vText : t.nvText, border: `1px solid ${GROUP_COLORS[(g.group_number-1)%GROUP_COLORS.length]}40` }}><span style={{ width:8, height:8, borderRadius:'50%', background:GROUP_COLORS[(g.group_number-1)%GROUP_COLORS.length], display:'inline-block' }}></span>Group {g.group_number} (n={g.group_size}) Mean={g.mean} Min={g.min}<VBadge pass={g.pass} t={t} /></div>)}</div>}
            </CardContent>
          </Card>
        );
      })}

      {/* ── EN 206 Cl. 8 ── */}
      {regime === 'EN 206 Cl. 8' && rc && (
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">EN 206 Cl. 8 — Production Control ({rc.n_results < 35 ? 'Initial' : 'Continuous'})</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto space-y-4">
            {rc.transition_notice && <div className="text-xs p-3 rounded" style={{ background: 'rgba(59,130,246,0.08)', color: '#3b82f6' }}>{rc.transition_notice}</div>}
            <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '11px', color: t.text }}>
              <thead><tr style={{ borderBottom: `2px solid ${t.tableBorder}` }}>{['#','fc28 (MPa)','fci ≥ fck-4','Group of 3','Criterion','Explanation','✓/✗'].map(h => <th key={h} className="px-2 py-2 text-left font-semibold" style={{ color: t.textMuted }}>{h}</th>)}</tr></thead>
              <tbody>
                {rc.n_results === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center" style={{ color: t.textDim }}>No results entered yet</td></tr>}
                {rc.conformity?.individual?.map((c, i) => {
                  const g3 = rc.conformity.group_of_3?.find(g => i >= g.group_start - 1 && i < g.group_start + 2);
                  const isG3Start = g3 && i === g3.group_start - 1;
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${t.tableBorder}`, background: !c.pass ? t.nvBg : i % 2 === 1 ? t.tableStripe : 'transparent' }}>
                      <td className="px-2 py-1.5" style={{ color: t.textDim }}>{c.index}</td>
                      <td className="px-2 py-1.5 font-mono font-bold">{c.value}</td>
                      <td className="px-2 py-1.5"><VBadge pass={c.pass} t={t} /></td>
                      {isG3Start ? <td className="px-2 py-1.5 font-mono" rowSpan={3} style={{ verticalAlign: 'middle' }}>{g3.mean} <VBadge pass={g3.pass} t={t} /></td> : (!g3 || i !== g3.group_start - 1) && i % 3 === 0 ? <td className="px-2 py-1.5" style={{ color: t.textDim }}>—</td> : null}
                      <td className="px-2 py-1.5 text-[10px]" style={{ color: t.textMuted }}>{!c.pass ? `fci = ${c.value} < fck-4 = ${c.required}` : isG3Start && g3 && !g3.pass ? `Mean ${g3.mean} < fck+4 = ${g3.required}` : 'Conforming'}</td>
                      <td className="px-2 py-1.5 text-[10px]" style={{ color: t.textDim }}>{!c.pass ? `Result ${c.value} MPa below fck-4 = ${c.required} MPa — individual failure` : isG3Start && g3 && !g3.pass ? `Rolling mean of 3 consecutive results = ${g3.mean} MPa — below fck + 4 MPa threshold of ${g3.required} MPa` : ''}</td>
                      <td className="px-2 py-1.5 text-center"><span style={{ color: c.pass ? t.vText : t.nvText, fontWeight: 700 }}>{c.pass ? '✓' : '✗'}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rc.conformity?.group_of_15 && <div className="text-xs p-3 rounded" style={{ background: rc.conformity.group_of_15.pass ? t.vBg : t.nvBg, color: rc.conformity.group_of_15.pass ? t.vText : t.nvText }}>Group of {rc.conformity.group_of_15.n}: mean = {rc.conformity.group_of_15.mean} ≥ fck + 1.48&sigma; = {rc.conformity.group_of_15.required} <span className="font-bold ml-2">{rc.conformity.group_of_15.pass ? '✓' : '✗'}</span>{!rc.conformity.group_of_15.pass && <span className="block mt-1">Mean of {rc.conformity.group_of_15.n} results ({rc.conformity.group_of_15.mean} MPa) is below the required threshold of {rc.conformity.group_of_15.required} MPa</span>}</div>}
            {rc.sigma_monitoring && <div className="text-xs p-3 rounded" style={{ background: rc.sigma_monitoring.within_bounds ? t.vBg : t.nvBg, color: rc.sigma_monitoring.within_bounds ? t.vText : t.nvText }}>Sigma monitoring: s<sub>n</sub> = {rc.sigma_monitoring.sn} — bounds [{rc.sigma_monitoring.lower_bound}, {rc.sigma_monitoring.upper_bound}] <span className="font-bold ml-2">{rc.sigma_monitoring.within_bounds ? '✓' : '✗'}</span></div>}
          </CardContent>
        </Card>
      )}

      {/* ── HK CoP SUC 2013 ── */}
      {regime === 'HK CoP SUC 2013' && rc && (
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">HK CoP SUC 2013 — {rc.criterion}, {rc.cube_size}mm</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto space-y-4">
            <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '11px', color: t.text }}>
              <thead><tr style={{ borderBottom: `2px solid ${t.tableBorder}` }}>{['Group','Results','Mean','Mean > fck+margin','Min','Min ≥ fck+margin','Explanation','✓/✗'].map(h => <th key={h} className="px-2 py-2 text-left font-semibold" style={{ color: t.textMuted }}>{h}</th>)}</tr></thead>
              <tbody>
                {(!rc.groups_of_4 || rc.groups_of_4.length === 0) && <tr><td colSpan={8} className="px-2 py-6 text-center" style={{ color: t.textDim }}>No results entered yet</td></tr>}
                {rc.groups_of_4?.map((g, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${t.tableBorder}`, background: !g.pass ? t.nvBg : i % 2 === 1 ? t.tableStripe : 'transparent' }}>
                    <td className="px-2 py-1.5 font-mono">{g.start}–{g.end}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{g.values.join(', ')}</td>
                    <td className="px-2 py-1.5 font-mono font-bold">{g.mean}</td>
                    <td className="px-2 py-1.5"><VBadge pass={g.mean_pass} t={t} /> <span className="text-[10px]" style={{ color: t.textDim }}>{g.mean} {'>'} {g.mean_required}</span></td>
                    <td className="px-2 py-1.5 font-mono">{g.min}</td>
                    <td className="px-2 py-1.5"><VBadge pass={g.min_pass} t={t} /> <span className="text-[10px]" style={{ color: t.textDim }}>{g.min} ≥ {g.min_required}</span></td>
                    <td className="px-2 py-1.5 text-[10px]" style={{ color: t.textDim }}>{!g.mean_pass ? `Rolling mean of 4 = ${g.mean} MPa — below fck+${rc.criterion === 'C1' ? '5' : '3'} = ${g.mean_required} MPa` : !g.min_pass ? `Individual min ${g.min} MPa below fck${g.min_required - fck} = ${g.min_required} MPa` : ''}</td>
                    <td className="px-2 py-1.5 text-center"><span style={{ color: g.pass ? t.vText : t.nvText, fontWeight: 700 }}>{g.pass ? '✓' : '✗'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rc.auto_switch_info && <div className="text-xs p-3 rounded" style={{ background: 'rgba(59,130,246,0.08)', color: '#3b82f6' }}>Auto-switch recommended: {rc.auto_switch_info.recommend} (&sigma;&#8324;&#8320; = {rc.auto_switch_info.sigma_40} vs threshold {rc.auto_switch_info.threshold})</div>}
            {rc.stop_work && <div className="text-xs p-3 rounded font-bold" style={{ background: t.nvBg, color: t.nvText }}>SUSPEND CONCRETING: {rc.stop_work.reason}</div>}
          </CardContent>
        </Card>
      )}

      {/* ── ACI 318 ── */}
      {regime === 'ACI 318' && rc && (
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">ACI 318 (cl. 26.12) — f'c = {rc.fc_prime} {rc.unit}</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto space-y-4">
            <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '11px', color: t.text }}>
              <thead><tr style={{ borderBottom: `2px solid ${t.tableBorder}` }}>{['#','fc28','fci ≥ floor','Rolling Avg of 3','Avg ≥ f\'c','Explanation','✓/✗'].map(h => <th key={h} className="px-2 py-2 text-left font-semibold" style={{ color: t.textMuted }}>{h}</th>)}</tr></thead>
              <tbody>
                {rc.n_results === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center" style={{ color: t.textDim }}>No results entered yet</td></tr>}
                {rc.individual_checks?.map((c, i) => {
                  const r3 = rc.rolling_3?.find(r => i === r.start - 1);
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${t.tableBorder}`, background: !c.pass ? t.nvBg : i % 2 === 1 ? t.tableStripe : 'transparent' }}>
                      <td className="px-2 py-1.5" style={{ color: t.textDim }}>{c.index}</td>
                      <td className="px-2 py-1.5 font-mono font-bold">{c.value}</td>
                      <td className="px-2 py-1.5"><VBadge pass={c.pass} t={t} /> <span className="text-[10px]" style={{ color: t.textDim }}>{c.value} ≥ {c.floor}</span></td>
                      <td className="px-2 py-1.5 font-mono">{r3 ? r3.mean : '—'}</td>
                      <td className="px-2 py-1.5">{r3 ? <VBadge pass={r3.pass} t={t} /> : '—'}</td>
                      <td className="px-2 py-1.5 text-[10px]" style={{ color: t.textDim }}>{!c.pass ? `Result ${c.value} ${rc.unit} below floor ${c.floor} ${rc.unit} — Initiate investigation per ACI 26.12.6` : r3 && !r3.pass ? `Rolling mean of 3 = ${r3.mean} ${rc.unit} below f'c = ${rc.fc_prime} ${rc.unit} — Steps required to increase subsequent test results` : ''}</td>
                      <td className="px-2 py-1.5 text-center"><span style={{ color: c.pass && (!r3 || r3.pass) ? t.vText : t.nvText, fontWeight: 700 }}>{c.pass && (!r3 || r3.pass) ? '✓' : '✗'}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ── FHWA / PWL ── */}
      {regime === 'FHWA / PWL' && rc && (
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">FHWA / PWL Statistical Compliance</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto space-y-4">
            {rc.method_b_lots?.length > 0 && (
              <div>
                <h4 className="text-[10px] font-semibold tracking-wider mb-2" style={{ color: t.sectionLabel }}>METHOD B — PWL PER LOT</h4>
                <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '11px', color: t.text }}>
                  <thead><tr style={{ borderBottom: `2px solid ${t.tableBorder}` }}>{['Lot','Results','n','X̄','s','Q','PWL','Threshold','Explanation','✓/✗'].map(h => <th key={h} className="px-2 py-2 text-left font-semibold" style={{ color: t.textMuted }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {rc.method_b_lots.map((lot, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${t.tableBorder}`, background: !lot.pass ? t.nvBg : i % 2 === 1 ? t.tableStripe : 'transparent' }}>
                        <td className="px-2 py-1.5 font-bold">{lot.lot_number}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px]">{lot.start}–{lot.end}</td>
                        <td className="px-2 py-1.5">{lot.n}</td>
                        <td className="px-2 py-1.5 font-mono font-bold">{lot.mean}</td>
                        <td className="px-2 py-1.5 font-mono">{lot.sd}</td>
                        <td className="px-2 py-1.5 font-mono">{lot.q_index ?? '—'}</td>
                        <td className="px-2 py-1.5 font-mono font-bold" style={{ color: lot.pass ? t.vText : t.nvText }}>{lot.pwl}%</td>
                        <td className="px-2 py-1.5">{lot.threshold}%</td>
                        <td className="px-2 py-1.5 text-[10px]" style={{ color: t.textDim }}>{!lot.pass ? `PWL = ${lot.pwl}% is below the ${lot.threshold}% acceptance threshold` : ''}</td>
                        <td className="px-2 py-1.5 text-center"><span style={{ color: lot.pass ? t.vText : t.nvText, fontWeight: 700 }}>{lot.pass ? '✓' : '✗'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {rc.method_a?.length > 0 && (
              <div>
                <h4 className="text-[10px] font-semibold tracking-wider mb-2" style={{ color: t.sectionLabel }}>METHOD A — RUNNING AVERAGE</h4>
                <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '11px', color: t.text }}>
                  <thead><tr style={{ borderBottom: `2px solid ${t.tableBorder}` }}>{['Group','Mean','≥ f\'c','✓/✗'].map(h => <th key={h} className="px-2 py-2 text-left font-semibold" style={{ color: t.textMuted }}>{h}</th>)}</tr></thead>
                  <tbody>{rc.method_a.map((r, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${t.tableBorder}` }}><td className="px-2 py-1.5">{r.start}–{r.start+2}</td><td className="px-2 py-1.5 font-mono font-bold">{r.mean}</td><td className="px-2 py-1.5"><VBadge pass={r.pass} t={t} /></td><td className="px-2 py-1.5 text-center"><span style={{ color: r.pass ? t.vText : t.nvText, fontWeight: 700 }}>{r.pass ? '✓' : '✗'}</span></td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {rc.n_results === 0 && <div className="py-6 text-center text-xs" style={{ color: t.textDim }}>No results entered yet</div>}
            {rc.sd_trend_flag && <div className="text-xs p-3 rounded" style={{ background: 'rgba(245,158,11,0.08)', color: '#f59e0b' }}>{rc.sd_trend_flag}</div>}
            {rc.running_sd?.length > 3 && (
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={rc.running_sd} margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={t.tableBorder} />
                    <XAxis dataKey="index" stroke={t.textDim} fontSize={9} />
                    <YAxis stroke={t.textDim} fontSize={9} />
                    <Line type="monotone" dataKey="sd" stroke="#f59e0b" strokeWidth={2} dot={false} name="Running σ" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Flags section — all regimes except Annex B */}
      {regime !== 'EN 206 Annex B' && rc?.flags?.length > 0 && (
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">Flags &amp; Alerts</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {rc.flags.map((f, i) => (
              <div key={i} className="flex items-start gap-2 text-xs p-2 rounded" style={{ background: f.type === 'fail' || f.type === 'stop_work' ? t.nvBg : 'rgba(245,158,11,0.08)', color: f.type === 'fail' || f.type === 'stop_work' ? t.nvText : '#f59e0b' }}>
                <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" /><span>{f.message}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ═══════════════ TAB 5: CHARTS & ANALYSIS (Expanded) ═══════════════ */
const AnalysisTab = ({ projectId, t }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCusum, setShowCusum] = useState(false);
  useEffect(() => { setLoading(true); fetchApi(`/api/it/projects/${projectId}/analysis`).then(setAnalysis).catch(() => {}).finally(() => setLoading(false)); }, [projectId]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: t.accent }} /></div>;
  if (!analysis) return null;
  const { fck, shewhart, prediction_7d_28d, std_dev, bayesian_projection, production_metrics, cusum_data } = analysis;

  return (
    <div className="space-y-6">
      {/* ══ 4A: Statistical Process Control ══ */}
      {shewhart && (
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ color: t.text }} className="text-base">{showCusum ? 'CUSUM Control Chart' : 'Process Control — Shewhart Chart'}</CardTitle>
                <p className="text-xs" style={{ color: t.textDim }}>TMS = {shewhart.tms} MPa &middot; &sigma; = {shewhart.sigma} &middot; n = {shewhart.n} &middot; CoV = {shewhart.cov}%</p>
              </div>
              <button onClick={() => setShowCusum(!showCusum)} className="text-[10px] font-semibold px-3 py-1.5 rounded" style={{ background: showCusum ? t.accent + '20' : t.inputBg, color: showCusum ? t.accent : t.textMuted, border: `1px solid ${showCusum ? t.accent + '40' : t.inputBorder}` }} data-testid="cusum-toggle">{showCusum ? 'Show Shewhart' : 'Show CUSUM'}</button>
            </div>
          </CardHeader>
          <CardContent>
            {!showCusum ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={shewhart.chart_data} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={t.tableBorder} />
                    <XAxis dataKey="index" stroke={t.textDim} />
                    <YAxis stroke={t.textDim} domain={['auto', 'auto']} />
                    <Tooltip contentStyle={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text, fontSize: 12 }} formatter={(v, name) => { const l = { value:'Strength', rolling_15:'Rolling 15 Avg', tms:'TMS', ucl:'UCL', uwl:'UWL', lwl:'LWL', lcl:'LCL', fck:'fck' }; return v != null ? [`${v} MPa`, l[name]||name] : null; }} />
                    <ReferenceLine y={shewhart.ucl} stroke="#ef4444" strokeDasharray="8 4" label={{ value:'UCL', fill:'#ef4444', fontSize:10 }} />
                    <ReferenceLine y={shewhart.uwl} stroke="#f59e0b" strokeDasharray="4 4" label={{ value:'UWL', fill:'#f59e0b', fontSize:10 }} />
                    <ReferenceLine y={shewhart.tms} stroke={t.accent} strokeDasharray="2 2" label={{ value:'TMS', fill:t.accent, fontSize:10 }} />
                    <ReferenceLine y={shewhart.lwl} stroke="#f59e0b" strokeDasharray="4 4" label={{ value:'LWL', fill:'#f59e0b', fontSize:10 }} />
                    <ReferenceLine y={shewhart.lcl} stroke="#ef4444" strokeDasharray="8 4" label={{ value:'LCL', fill:'#ef4444', fontSize:10 }} />
                    <ReferenceLine y={fck} stroke="#6366f1" strokeDasharray="2 6" label={{ value:'fck', fill:'#6366f1', fontSize:10 }} />
                    <Line type="monotone" dataKey="value" stroke={t.accent} strokeWidth={2} dot={{ fill: t.accent, r: 3 }} />
                    <Line type="monotone" dataKey="rolling_15" stroke="#3b82f6" strokeWidth={2.5} dot={false} connectNulls={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : cusum_data?.length > 0 && (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cusum_data} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={t.tableBorder} />
                    <XAxis dataKey="index" stroke={t.textDim} />
                    <YAxis stroke={t.textDim} domain={['auto', 'auto']} />
                    <Tooltip contentStyle={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text, fontSize: 12 }} />
                    <ReferenceLine y={cusum_data[0]?.h_upper} stroke="#ef4444" strokeDasharray="8 4" label={{ value:'Action Limit (+)', fill:'#ef4444', fontSize:10 }} />
                    <ReferenceLine y={cusum_data[0]?.h_lower} stroke="#ef4444" strokeDasharray="8 4" label={{ value:'Action Limit (-)', fill:'#ef4444', fontSize:10 }} />
                    <ReferenceLine y={0} stroke={t.textDim} strokeDasharray="2 2" />
                    <Line type="monotone" dataKey="cusum_pos" stroke={t.accent} strokeWidth={2} dot={false} name="CUSUM+" />
                    <Line type="monotone" dataKey="cusum_neg" stroke="#f59e0b" strokeWidth={2} dot={false} name="CUSUM-" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {shewhart.alerts?.length > 0 && <div className="mt-3 space-y-1">{shewhart.alerts.map((a, i) => <div key={i} className="text-xs flex items-center gap-1" style={{ color: a.type === 'action' ? '#ef4444' : '#f59e0b' }}><AlertTriangle className="w-3 h-3" /> {a.message}</div>)}</div>}
          </CardContent>
        </Card>
      )}

      {/* ══ 4C: Production Efficiency Metrics ══ */}
      {production_metrics && (
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }} data-testid="production-metrics">
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">Production Efficiency</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {production_metrics.strength_efficiency != null && (
                <div className="text-center p-3 rounded" style={{ background: t.tableStripe }}>
                  <div className="text-lg font-bold" style={{ color: t.accent }}>{production_metrics.strength_efficiency}</div>
                  <div className="text-[10px]" style={{ color: t.textDim }}>Strength Efficiency</div>
                  <div className="text-[9px]" style={{ color: t.textDim }}>MPa&middot;m&sup3;/kg</div>
                </div>
              )}
              <div className="text-center p-3 rounded" style={{ background: t.tableStripe }}>
                <div className="text-lg font-bold" style={{ color: production_metrics.over_design_margin > 10 ? '#f59e0b' : t.accent }}>{production_metrics.over_design_margin}</div>
                <div className="text-[10px]" style={{ color: t.textDim }}>Over-design Margin</div>
                <div className="text-[9px]" style={{ color: t.textDim }}>MPa above fck</div>
              </div>
              {production_metrics.cost_of_variability != null && (
                <div className="text-center p-3 rounded" style={{ background: t.tableStripe }}>
                  <div className="text-lg font-bold" style={{ color: t.text }}>${production_metrics.cost_of_variability}</div>
                  <div className="text-[10px]" style={{ color: t.textDim }}>Cost of Variability</div>
                  <div className="text-[9px]" style={{ color: t.textDim }}>$/m&sup3;</div>
                </div>
              )}
              <div className="text-center p-3 rounded" style={{ background: production_metrics.variability_warning ? 'rgba(239,68,68,0.08)' : t.tableStripe }}>
                <div className="text-lg font-bold" style={{ color: production_metrics.variability_warning ? '#ef4444' : t.text }}>{production_metrics.variability_ratio}%</div>
                <div className="text-[10px]" style={{ color: t.textDim }}>Variability Ratio (CoV)</div>
                {production_metrics.variability_warning && <div className="text-[9px] font-bold" style={{ color: '#ef4444' }}>{'>'} 10% warning</div>}
              </div>
              {production_metrics.actual_7d_28d_ratio != null && (
                <div className="text-center p-3 rounded" style={{ background: t.tableStripe }}>
                  <div className="text-lg font-bold" style={{ color: t.text }}>{production_metrics.actual_7d_28d_ratio}</div>
                  <div className="text-[10px]" style={{ color: t.textDim }}>Average 7d/28d Ratio (actual)</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ══ 4B: Model Learning Progress (Bayesian Convergence) ══ */}
      {bayesian_projection && (
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">Model Learning Progress</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs" style={{ color: t.textMuted }}>
              Based on <strong style={{ color: t.text }}>{bayesian_projection.project_pairs}</strong> data pairs, the model predicts this mix behaves <strong style={{ color: t.accent }}>{bayesian_projection.convergence_data?.length > 0 && bayesian_projection.convergence_data[bayesian_projection.convergence_data.length - 1]?.beta > 1.5 ? 'above' : bayesian_projection.convergence_data?.length > 0 && bayesian_projection.convergence_data[bayesian_projection.convergence_data.length - 1]?.beta > 1.4 ? 'in line with' : 'below'}</strong> the cement family baseline.
            </p>
            {bayesian_projection.convergence_data?.length > 1 && (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={bayesian_projection.convergence_data} margin={{ top: 5, right: 20, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={t.tableBorder} />
                    <XAxis dataKey="pairs" stroke={t.textDim} fontSize={10} label={{ value: 'Data pairs', position: 'insideBottom', offset: -5, fill: t.textDim, fontSize: 9 }} />
                    <YAxis stroke={t.textDim} fontSize={10} domain={['auto', 'auto']} label={{ value: 'β', angle: -90, position: 'insideLeft', fill: t.textDim, fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text, fontSize: 11 }} />
                    <ReferenceLine y={1.47} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: 'Prior β=1.47', fill: '#f59e0b', fontSize: 9 }} />
                    <Area dataKey="beta_upper" stroke="none" fill="rgba(16,185,129,0.1)" />
                    <Area dataKey="beta_lower" stroke="none" fill={t.bg} />
                    <Line type="monotone" dataKey="beta" stroke={t.accent} strokeWidth={2} dot={{ fill: t.accent, r: 3 }} name="β estimate" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ══ 4D: Mix Behaviour Profile ══ */}
      {bayesian_projection && bayesian_projection.project_pairs >= 3 && (
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">Mix Behaviour Profile</CardTitle></CardHeader>
          <CardContent>
            {bayesian_projection.convergence_data?.length > 0 && (() => {
              const lastBeta = bayesian_projection.convergence_data[bayesian_projection.convergence_data.length - 1]?.beta;
              const isDeviating = lastBeta && Math.abs(lastBeta - 1.47) > 0.1;
              return <p className="text-xs" style={{ color: isDeviating ? '#f59e0b' : t.textMuted }}>
                {isDeviating ? "This project's mix behaviour is deviating from the cement family baseline — review mix design or source consistency."
                : "This project's mix is performing in line with the expected cement family behaviour."}
              </p>;
            })()}
          </CardContent>
        </Card>
      )}

      {/* Std Dev + 7d/28d Regression */}
      <div className="grid md:grid-cols-2 gap-6">
        {std_dev && (
          <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
            <CardHeader><CardTitle style={{ color: t.text }} className="text-base">Standard Deviation</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {[['n', std_dev.n, 'Results'], ['mean', `${std_dev.mean}`, 'Mean (MPa)'], ['sigma', `${std_dev.sigma}`, 'Std Dev (MPa)'], ['cov', `${std_dev.cov}%`, 'CoV']].map(([k, v, l]) => (
                  <div key={k} className="text-center p-3 rounded" style={{ background: t.tableStripe }}>
                    <div className="text-xl font-bold" style={{ color: k === 'n' ? t.accent : t.text }}>{v}</div>
                    <div className="text-[10px]" style={{ color: t.textDim }}>{l}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {prediction_7d_28d?.sufficient_data && (
          <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
            <CardHeader><CardTitle style={{ color: t.text }} className="text-base">7d &rarr; 28d Regression</CardTitle></CardHeader>
            <CardContent>
              <div className="text-xs mb-3" style={{ color: t.textMuted }}><strong className="font-mono" style={{ color: t.text }}>{prediction_7d_28d.equation}</strong> &middot; R&sup2; = {prediction_7d_28d.r_squared} &middot; {prediction_7d_28d.pairs_count} pairs</div>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 5, right: 15, left: 5, bottom: 25 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={t.tableBorder} />
                    <XAxis dataKey="fc7" type="number" stroke={t.textDim} fontSize={10} label={{ value: '7d (MPa)', position: 'insideBottom', offset: -10, fill: t.textDim, fontSize: 10 }} />
                    <YAxis dataKey="fc28" type="number" stroke={t.textDim} fontSize={10} label={{ value: '28d', angle: -90, position: 'insideLeft', fill: t.textDim, fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text, fontSize: 11 }} />
                    <Scatter data={prediction_7d_28d.scatter_data} fill={t.accent} r={3} />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {analysis.total_samples === 0 && <div className="text-center py-8" style={{ color: t.textDim }}>Enter data in the Samples tab to see analysis.</div>}
    </div>
  );
};

/* ═══════════════ TAB 8: COMPLIANCE REPORT ═══════════════ */
const ComplianceReportTab = ({ projectId, t }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); fetchApi(`/api/it/projects/${projectId}/analysis`).then(setData).catch(() => {}).finally(() => setLoading(false)); }, [projectId]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: t.accent }} /></div>;
  if (!data) return null;

  const { project, fck, total_samples, regime_compliance, compliance_regime, report_data, shewhart, std_dev, projection_table } = data;
  const rd = report_data || {};
  const n = total_samples;
  const regime = compliance_regime || 'EN 206 Annex B';
  const rc = regime_compliance;

  // Determine overall status
  let overallStatus = 'INCONCLUSIVE';
  let statusColor = '#f59e0b';
  if (n === 0) { overallStatus = 'INCONCLUSIVE'; statusColor = '#f59e0b'; }
  else if (regime === 'EN 206 Annex B') {
    const allPass = data.pour_identity_tests?.every(p => p.identity_result?.overall_pass !== false);
    overallStatus = allPass ? 'COMPLIANT' : 'NON-COMPLIANT';
    statusColor = allPass ? t.vText : t.nvText;
  } else if (rc) {
    overallStatus = rc.overall_pass ? 'COMPLIANT' : 'NON-COMPLIANT';
    statusColor = rc.overall_pass ? t.vText : t.nvText;
  }

  // Minimum results per regime
  const minResults = { 'EN 206 Annex B': 1, 'EN 206 Cl. 8': 3, 'HK CoP SUC 2013': 4, 'ACI 318': 3, 'FHWA / PWL': 5 };
  const minReq = minResults[regime] || 3;
  const samplingAdequate = n >= minReq;

  // Early warnings
  const earlyWarnings = rd.early_warnings_issued || [];

  // Shewhart violations
  const shewhartViolations = rd.shewhart_violations || [];

  // Conformity summary
  const conformingCount = rd.total_conforming || n;
  const nonConformingCount = rd.total_non_conforming || 0;

  const handlePrint = () => { window.print(); };

  return (
    <div className="space-y-6">
      {/* Print button */}
      <div className="flex justify-end print:hidden">
        <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="export-pdf-btn">
          <Printer className="w-4 h-4 mr-2" /> Export PDF
        </Button>
      </div>

      {/* Report content */}
      <div id="compliance-report" className="space-y-6 print:space-y-4" data-testid="compliance-report">
        {/* Header Block */}
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-xl font-bold" style={{ color: t.text }}>{project?.project_name || 'Untitled Project'}</h1>
                <div className="mt-2 space-y-1 text-xs" style={{ color: t.textMuted }}>
                  <div>Contractor: <strong style={{ color: t.text }}>{project?.contractor || '—'}</strong></div>
                  <div>f<sub>ck</sub>: <strong style={{ color: t.text }}>{fck} MPa</strong></div>
                  <div>Exposure: <strong style={{ color: t.text }}>{project?.exposure_class || '—'}</strong></div>
                  <div>Compliance Regime: <strong style={{ color: t.text }}>{regime}</strong></div>
                  <div>Report Generated: <strong style={{ color: t.text }}>{new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></div>
                </div>
              </div>
              <div className="text-center px-4 py-3 rounded" style={{ background: overallStatus === 'COMPLIANT' ? t.vBg : overallStatus === 'NON-COMPLIANT' ? t.nvBg : 'rgba(245,158,11,0.1)', border: `1px solid ${statusColor}30` }}>
                <div className="text-xs font-semibold" style={{ color: t.textDim }}>Overall Status</div>
                <div className="text-lg font-bold" style={{ color: statusColor }}>{overallStatus}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 1 — Sampling Adequacy */}
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">1. Sampling Adequacy</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span style={{ color: t.textMuted }}>Results entered: <strong style={{ color: t.text }}>{n}</strong></span>
              <span style={{ color: t.textMuted }}>Minimum required ({regime}): <strong style={{ color: t.text }}>{minReq}</strong></span>
              <span style={{ color: samplingAdequate ? t.vText : t.nvText, fontWeight: 700 }}>{samplingAdequate ? '✓ Adequate' : '✗ Insufficient'}</span>
            </div>
            {!samplingAdequate && <p className="text-xs" style={{ color: '#f59e0b' }}>Insufficient results have been entered to make a full conformity determination. A minimum of {minReq} results is required under {regime}.</p>}
          </CardContent>
        </Card>

        {/* Section 2 — Strength Conformity */}
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">2. Strength Conformity</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            {rc?.flags?.length > 0 && (
              <div className="space-y-1.5">
                {rc.flags.slice(0, 10).map((f, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs p-2 rounded" style={{ background: f.type === 'fail' || f.type === 'stop_work' ? t.nvBg : 'rgba(245,158,11,0.08)', color: f.type === 'fail' || f.type === 'stop_work' ? t.nvText : '#f59e0b' }}>
                    <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" /><span>{f.message}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs" style={{ color: t.textMuted }}>
              {n > 0 ? `${conformingCount} of ${n} test results fully comply. ${nonConformingCount > 0 ? `${nonConformingCount} result${nonConformingCount > 1 ? 's' : ''} triggered non-conformity criteria.` : 'No non-conformity criteria triggered.'}` : 'No test results entered.'}
            </p>
          </CardContent>
        </Card>

        {/* Section 3 — Early Warnings Issued */}
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">3. Early Warnings Issued</CardTitle></CardHeader>
          <CardContent className="text-sm">
            {earlyWarnings.length === 0 ? (
              <p className="text-xs" style={{ color: t.textMuted }}>No early warnings issued during this project.</p>
            ) : (
              <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '11px', color: t.text }}>
                <thead><tr style={{ borderBottom: `2px solid ${t.tableBorder}` }}>{['Sample', 'f7', 'Projected f28', 'P(f28<fck-4)'].map(h => <th key={h} className="px-2 py-2 text-left" style={{ color: t.textMuted }}>{h}</th>)}</tr></thead>
                <tbody>{earlyWarnings.map((w, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${t.tableBorder}`, background: t.nvBg }}>
                    <td className="px-2 py-1.5 font-mono">{w.sample_id}</td>
                    <td className="px-2 py-1.5 font-mono">{w.fc7}</td>
                    <td className="px-2 py-1.5 font-mono">{w.projected_f28}</td>
                    <td className="px-2 py-1.5 font-bold" style={{ color: t.nvText }}>{w.early_warning_pct}%</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Section 4 — Process Stability */}
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">4. Process Stability</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {shewhartViolations.length === 0 ? (
              <p className="text-xs" style={{ color: t.vText }}>Production process remained within control limits throughout the project.</p>
            ) : (
              <div className="space-y-1">
                <p className="text-xs" style={{ color: '#f59e0b' }}>The following control limit violations were detected:</p>
                {shewhartViolations.map((v, i) => (
                  <div key={i} className="text-xs p-2 rounded flex items-start gap-2" style={{ background: 'rgba(245,158,11,0.08)', color: '#f59e0b' }}>
                    <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" /><span>{v}</span>
                  </div>
                ))}
              </div>
            )}
            {std_dev && <p className="text-xs" style={{ color: t.textMuted }}>Standard deviation: &sigma; = {std_dev.sigma} MPa, CoV = {std_dev.cov}%{rd.sigma_within_bounds ? ' — within normative bounds.' : ' — outside normative bounds.'}</p>}
          </CardContent>
        </Card>

        {/* Section 5 — Conclusion */}
        <Card style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <CardHeader><CardTitle style={{ color: t.text }} className="text-base">5. Conclusion</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm" style={{ color: t.text }}>
              {overallStatus === 'COMPLIANT' && `Based on ${n} test results entered for project "${project?.project_name}", concrete of grade ${fck} MPa assessed under ${regime} is declared COMPLIANT. All applicable conformity criteria have been satisfied.`}
              {overallStatus === 'NON-COMPLIANT' && `Based on ${n} test results entered for project "${project?.project_name}", concrete of grade ${fck} MPa assessed under ${regime} is declared NON-COMPLIANT. The following criteria were not satisfied: ${(rc?.flags || []).filter(f => f.type === 'fail').map(f => f.message).slice(0, 3).join('; ')}.`}
              {overallStatus === 'INCONCLUSIVE' && `Insufficient results have been entered to make a full conformity determination for project "${project?.project_name}". A minimum of ${minReq} results is required under ${regime}. Currently ${n} results entered.`}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Print-specific styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #compliance-report, #compliance-report * { visibility: visible; }
          #compliance-report { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
};

